'use client';

import { Fragment, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot, Slottable } from '../slot';
import { AccordionHeaderContent } from './accordion-header-content';
import { AccordionHeaderTrigger } from './accordion-header-trigger';
import { AccordionItemContext } from './context/accordion-item.context';
import { accordionHeaderRecipe } from './styles/accordion-header.styles';
import type { AccordionHeaderPropsInterface } from './types/accordion-header.types';

const defaultRenderTrigger: NonNullable<AccordionHeaderPropsInterface['renderTrigger']> = (
  triggerProps,
) => <AccordionHeaderTrigger {...triggerProps} />;

const defaultRenderContent: NonNullable<AccordionHeaderPropsInterface['renderContent']> = (
  contentProps,
) => <AccordionHeaderContent {...contentProps} />;

/**
 * Interactive heading text that hides or reveals the content in its sibling
 * `Accordion.Panel`.
 *
 * Selection uses an empty stretched native `<button>` so header content is not
 * nested inside the control. Prefer content text for the accessible name via
 * `aria-labelledby`; pass `aria-label` to override.
 *
 * @since 8.43.0
 */
export const AccordionHeader = forwardRef<HTMLDivElement, AccordionHeaderPropsInterface>(
  (props: AccordionHeaderPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      onClick,
      renderContent = defaultRenderContent,
      renderTrigger = defaultRenderTrigger,
      'aria-label': ariaLabel,
      ...rest
    } = props;

    const { disabled, expanded } = useContext(AccordionItemContext);

    const styles = css(accordionHeaderRecipe.raw({ expanded, disabled }), cssProp);

    const Component = asChild ? Slot : 'div';

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="Accordion.Header"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_accordion__header', styles, className)}
        {...rest}
      >
        <Slottable child={children}>
          {(slottable) => (
            <Fragment>
              {renderTrigger({ 'aria-label': ariaLabel, onClick })}
              {renderContent({ children: slottable })}
            </Fragment>
          )}
        </Slottable>
      </Component>
    );
  },
);

AccordionHeader.displayName = 'Accordion.Header';
